import Singleton from '../Singleton';
import CoreAppService from '../_shared/api/coreAppService';
import {
  NegotiationCreateRequest,
  NegotiationProposal,
  NegotiationProposalUpdateResponse,
  NegotiationProposalUpdateRequest
} from '../global.d';
import TradeService from './TradeService';
import { TradeContext, TradeState } from '../_shared/types/trade';
import { NegotiationStateShort } from '../_shared/models/NegotiationList';

export default class NegotiationService extends Singleton<NegotiationService> {
  public getProposals = async (auctionId: number) => {
    const client = await CoreAppService();
    try {
      const response = await client.negotiation.listNegotiationProposal(
        auctionId
      );
      return response?.data;
    } catch (e) {
      return null;
    }
  };

  public createProposal = async (
    auctionId: number,
    proposal: NegotiationCreateRequest,
    stayfirm: boolean = false
  ) => {
    const client = await CoreAppService();

    const { comment, price } = proposal;
    let errorMessage = null;
    let errorCode = null;

    const createRequest = {
      comment,
      stayFirm: stayfirm,
      price
    };

    const response = await client.negotiation
      .createNegotiationProposal(auctionId, createRequest)
      .catch(e => {
        errorMessage = e.response?.data?.message;
        errorCode = e.response?.data?.code;
      });
    return {
      errorMessage,
      errorCode,
      proposal: response?.data
    };
  };

  public stayFirm = async (
    auctionId: number,
    proposal: NegotiationCreateRequest
  ): Promise<NegotiationProposal> => {
    const client = await CoreAppService();

    const { comment, price } = proposal;
    const createRequest = {
      comment,
      stayFirm: true,
      price
    };

    const response = await client.negotiation.createNegotiationProposal(
      auctionId,
      createRequest
    );
    return response?.data;
  };

  public acceptProposal = async (
    auctionId: number
  ): Promise<NegotiationProposalUpdateResponse> => {
    const client = await CoreAppService();
    const requestBody: NegotiationProposalUpdateRequest = {
      state: 'ACCEPTED'
    };
    const response = await client.negotiation.updateNegotiationProposal(
      auctionId,
      requestBody
    );
    return response?.data;
  };

  public rejectProposal = async (
    auctionId: number
  ): Promise<NegotiationProposalUpdateResponse> => {
    const client = await CoreAppService();
    const requestBody: NegotiationProposalUpdateRequest = {
      state: 'REJECTED'
    };
    const response = await client.negotiation.updateNegotiationProposal(
      auctionId,
      requestBody
    );
    return response?.data;
  };

  public getNegotiationMessage = async (
    associatedTradeId,
    tradeContext,
    setNegotiationErrorMessage,
    setIsNegotiationErrorModalOpen,
    setTutorialNegotiationModalIsOpen
  ) => {
    if (tradeContext === TradeContext.Sell && associatedTradeId) {
      const isAssociatedTradeActive = stateNum =>
        stateNum === TradeState.active;
      const isAssociatedTradePending = stateNum =>
        stateNum === TradeState.pending;

      return TradeService.get()
        .getAssociatedTradeDetails(associatedTradeId, [
          'auctionSummary',
          'tradeState'
        ])
        .then(details => {
          let errorMessage = '';

          if (isAssociatedTradeActive(details?.tradeState)) {
            errorMessage = 'list.openTrade.dgnActive.negotiate.error.message';
            setNegotiationErrorMessage(errorMessage);
            setIsNegotiationErrorModalOpen(true);
          } else if (isAssociatedTradePending(details?.tradeState)) {
            this.getProposals(details?.auctionSummary?.id).then(res => {
              if (
                res &&
                res.negotiationState !== NegotiationStateShort.NO_NEGOTIATION
              ) {
                errorMessage = 'list.openTrade.dgn.negotiate.error.message';
              } else if (
                res &&
                res.negotiationState === NegotiationStateShort.NO_NEGOTIATION
              ) {
                errorMessage =
                  'list.openTrade.dgn.negotiateFirst.error.message';
              }
              setNegotiationErrorMessage(errorMessage);
              setIsNegotiationErrorModalOpen(true);
            });
          } else {
            setTutorialNegotiationModalIsOpen(true);
          }
        });
    }
    return setTutorialNegotiationModalIsOpen(true);
  };
}
